/**
 * Route-level role guard. Renders `<ForbiddenPage>` when the authenticated
 * user's role is below the required minimum; renders children otherwise.
 *
 * Shell: (desktop). Wrap restricted routes in the router with this component.
 *
 * Non-obvious contracts:
 *   - Only call this inside an authenticated tree (inside RequireAuth). It does
 *     not handle unauthenticated state — RequireAuth does that.
 *   - `minRole='manager'` allows manager + owner. `minRole='owner'` allows
 *     owner only. Staff is the baseline (no RequireRole needed for staff routes).
 *   - Server-side enforcement (RLS + Edge Functions) is the real security
 *     boundary; this gate is a UX affordance that prevents confusing errors.
 */
import type { ReactNode } from 'react';
import { useSession } from '../lib/store';
import { ForbiddenPage } from './ForbiddenPage';

type MinRole = 'manager' | 'owner';

export interface RequireRoleProps {
  minRole: MinRole;
  children: ReactNode;
}

function roleRank(role: string | null): number {
  if (role === 'owner') return 3;
  if (role === 'manager') return 2;
  return 1; // staff or null
}

export function RequireRole({ minRole, children }: RequireRoleProps) {
  const role = useSession((s) => s.role);

  if (roleRank(role) < roleRank(minRole)) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
}
